import React, { useState, useRef } from 'react';
import classes from './Login.module.css';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth, db, CATEGORIES } from '../config';
import { collection, doc, setDoc, addDoc, updateDoc, where, query, Timestamp, getDocs } from 'firebase/firestore';
import { generateId, sendNotification } from '../config/helpers';
import Loading from '../components/UI/Loading';
import ReactDOM from 'react-dom';

const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M19 12H5M5 12L11 18M5 12L11 6" stroke="#2c3030" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const EyeIcon = ({ visible }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    {visible ? (
      <>
        <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="#abacac" strokeWidth="1.8"/>
        <circle cx="12" cy="12" r="3" stroke="#abacac" strokeWidth="1.8"/>
      </>
    ) : (
      <>
        <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="#abacac" strokeWidth="1.8"/>
        <circle cx="12" cy="12" r="3" stroke="#abacac" strokeWidth="1.8"/>
        <line x1="3" y1="3" x2="21" y2="21" stroke="#abacac" strokeWidth="1.8" strokeLinecap="round"/>
      </>
    )}
  </svg>
);

const isEmail = (email) => {
  const regex = /^[_a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0-9-]+(\.[_a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0-9-]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*(\.[a-zA-Z]{2,4})$/;
  return regex.test(email);
};

const Login = ({ initialMode = 'signup', onBack }) => {
  const emailRef = useRef();
  const nameRef = useRef();
  const passwordRef = useRef();
  const [working, setWorking] = useState(false);
  const [mode, setMode] = useState(initialMode);
  const [agree, setAgree] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const addNewUser = async (user, enteredEmail, enteredName) => {
    try {
      await setDoc(doc(db, "users", user.uid), {
        id: user.uid,
        email: enteredEmail,
        name: enteredName,
        receiveNotifications: false,
        activeOrganiser: false,
        createdAt: Timestamp.fromDate(new Date())
      });
      return true;
    } catch (err) {
      setErrorMessage(err.message);
      return false;
    }
  };

  const addNewGroup = async (user, enteredName) => {
    try {
      // Check if festival was pre-selected as guest
      const savedFestival = localStorage.getItem('selectedFestival');
      const festival = savedFestival ? JSON.parse(savedFestival) : null;
      if (savedFestival) localStorage.removeItem('selectedFestival');

      const newGroupRef = await addDoc(collection(db, 'groups'), {
        id: '123',
        owner: user.uid,
        memberIds: [user.uid],
        memberEmails: [user.email],
        categories: CATEGORIES,
        festival: festival || null,
        members: [{ id: user.uid, email: user.email, name: enteredName, avatar: 1, editor: true, owner: true }],
        createdAt: Timestamp.fromDate(new Date())
      });
      if (newGroupRef?.id) {
        const groupRef = doc(db, 'groups', newGroupRef.id);
        await updateDoc(groupRef, { id: newGroupRef.id });
      }
      return true;
    } catch (err) {
      setErrorMessage(err.message);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    const enteredEmail = emailRef.current?.value?.toLowerCase();

    if (mode === 'reset') {
      if (!enteredEmail) { setErrorMessage('Please enter your email'); return; }
      setWorking(true);
      sendPasswordResetEmail(auth, enteredEmail)
        .then(() => { setWorking(false); setMode('login'); setErrorMessage('Password reset email sent!'); })
        .catch(err => { setErrorMessage(err.message); setWorking(false); });
      return;
    }

    if (!enteredEmail || !isEmail(enteredEmail)) { setErrorMessage('Please enter a valid email'); return; }
    const enteredPassword = passwordRef.current?.value;
    if (!enteredPassword) { setErrorMessage('Please enter a password'); return; }

    setWorking(true);

    if (mode === 'login') {
      signInWithEmailAndPassword(auth, enteredEmail, enteredPassword)
        .catch(err => {
          const msgs = {
            'auth/user-not-found': "We can't find a user with this email",
            'auth/wrong-password': 'Password is incorrect',
            'auth/too-many-requests': 'Too many attempts. Try again later.',
          };
          setErrorMessage(msgs[err.code] || err.message);
          setWorking(false);
        });
    } else {
      const enteredName = nameRef.current?.value;
      if (!enteredName) { setErrorMessage('Please enter your name'); setWorking(false); return; }
      if (!agree) { setErrorMessage('Please agree to the Terms & Conditions'); setWorking(false); return; }

      createUserWithEmailAndPassword(auth, enteredEmail, enteredPassword)
        .then(async (userCredential) => {
          await addNewUser(userCredential.user, enteredEmail, enteredName);
          const userGroups = [];
          const q = query(collection(db, "groups"), where("memberEmails", "array-contains", enteredEmail));
          const querySnapshot = await getDocs(q);
          querySnapshot.forEach(doc => userGroups.push(doc.data()));
          if (userGroups.length === 0) await addNewGroup(userCredential.user, enteredName);
          userGroups.forEach(group => sendNotification({
            id: generateId(20), status: 'unread', request: false,
            recipientId: group.owner, senderName: enteredName, senderEmail: enteredEmail,
            text: `${enteredName} has joined.`, createdAt: Timestamp.fromDate(new Date())
          }));
          setWorking(false);
        })
        .catch(err => {
          const msgs = {
            'auth/email-already-in-use': 'This email is already in use.',
            'auth/weak-password': 'Password should be at least 6 characters.',
          };
          setErrorMessage(msgs[err.code] || err.message);
          setWorking(false);
        });
    }
  };

  if (working) return <Loading msg='Hold on one sec...' />;

  return (
    <div className="app-wrapper">
    <div className={classes.container}>

      {/* Header */}
      <div className={classes.header}>
        <button className={classes.backBtn} onClick={onBack}>
          <BackIcon />
        </button>
      </div>

      <div className={classes.content}>

        {/* Heading */}
        <div className={classes.heading}>
          <h1 className={classes.title}>
            {mode === 'signup' ? 'Create your account' : mode === 'login' ? 'Welcome back!' : 'Reset password'}
          </h1>
          <p className={classes.subtitle}>
            {mode === 'signup' ? 'Your festival kit, sorted.' :
             mode === 'login' ? 'Sign in to continue' :
             'Enter your email to reset your password'}
          </p>
        </div>

        {/* Form */}
        <form className={classes.form} onSubmit={handleSubmit}>

          {mode === 'signup' && (
            <input
              ref={nameRef}
              className={classes.input}
              type="text"
              placeholder="Name"
              spellCheck="false"
              autoComplete="name"
            />
          )}

          <input
            ref={emailRef}
            className={classes.input}
            type="email"
            placeholder="Email"
            spellCheck="false"
            autoComplete="email"
          />

          {mode !== 'reset' && (
            <div className={classes.passwordWrapper}>
              <input
                ref={passwordRef}
                className={classes.input}
                type={passwordVisible ? 'text' : 'password'}
                placeholder="Password"
                spellCheck="false"
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              />
              <button
                type="button"
                className={classes.eyeBtn}
                onClick={() => setPasswordVisible(!passwordVisible)}
              >
                <EyeIcon visible={passwordVisible} />
              </button>
            </div>
          )}

          {mode === 'signup' && (
            <label className={classes.checkboxLabel}>
              <span className={classes.checkboxHitArea}>
                <input
                  type="checkbox"
                  checked={agree}
                  onChange={e => setAgree(e.target.checked)}
                  className={classes.checkbox}
                />
              </span>
              <span>
                By creating an account, you agree to our{' '}
                <a href="https://www.campingbuddy.com.au/terms_of_service" target="_blank" rel="noreferrer" className={classes.link}>
                  Terms & Conditions
                </a>
              </span>
            </label>
          )}

          {errorMessage && (
            <p className={classes.error}>{errorMessage}</p>
          )}

          <div className={classes.spacer} />

          <button type="submit" className={classes.submitBtn}>
            {mode === 'signup' ? 'Sign Up' : mode === 'login' ? 'Sign in' : 'Send reset email'}
          </button>

          {mode === 'signup' && (
            <p className={classes.switchText}>
              Already have an account?{' '}
              <button type="button" className={classes.switchBtn} onClick={() => setMode('login')}>
                Sign in
              </button>
            </p>
          )}

          {mode === 'login' && (
            <>
              <p className={classes.switchText}>
                Don't have an account?{' '}
                <button type="button" className={classes.switchBtn} onClick={() => setMode('signup')}>
                  Sign up
                </button>
              </p>
              <button type="button" className={classes.forgotBtn} onClick={() => setMode('reset')}>
                Forgot password?
              </button>
            </>
          )}

          {mode === 'reset' && (
            <p className={classes.switchText}>
              <button type="button" className={classes.switchBtn} onClick={() => setMode('login')}>
                Back to sign in
              </button>
            </p>
          )}

        </form>
      </div>
    </div>
    </div>
  );
};

export default Login;
